import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource, MatTable } from '@angular/material/table';
import { combineLatest, Subject, Subscription } from 'rxjs';
import { first, map, takeUntil } from 'rxjs/operators';

import { ApplicationService, NomineeTypeInfo, NominationWindowInfo, WindowTypeInfo, FormTemplateInfo } from 'services/application/application.service';
import { AwardsService, AwardProgramInfo } from 'services/awards/awards.service';
import { DialogService } from 'services/dialogs/dialogs.service';
import { ErrorService } from 'services/error/error.service';
import { ScreenSizeService } from 'services/screensize/screensize.service';
import { SplashService } from 'services/splash/splash.service';
import { UserInfo, UserService } from 'services/user/user.service';
import { DateFnsPipe } from 'pipes/datefns.pipe';
import { NomineeTypePipe } from 'pipes/nomineetype.pipe';
import { WindowTypePipe } from 'pipes/windowtype.pipe';
import { FormTemplatePipe } from '../../pipes/formtemplate.pipe';
import { SettingsService } from '../../services/settings/settings.service';

@Component({
    selector: 'app-manage-windows',
    styleUrls: ['windows.component.scss'],
    templateUrl: 'windows.component.html'
})

export class ManageWindowsComponent implements OnInit, OnDestroy {
    @ViewChild(MatTable, { read: false }) table: MatTable<NominationWindowInfo>;
    @ViewChild('paginator') paginator: MatPaginator;
    @ViewChild(MatSort) sorter: MatSort;

    private _subrelease: Subject<void> = new Subject<void>();

    AllHeaders: string[] = ['NomineeTypeName', 'WindowTypeName', 'DateBegin', 'DateEnd', 'Actions'];
    AvailableFormTemplates: FormTemplateInfo[];
    AvailableNomineeTypes: NomineeTypeInfo[];
    AvailableWindowTypes: WindowTypeInfo[];
    AwardInfo: AwardProgramInfo;
    CurrentSchoolYear: string;
    Windows: NominationWindowInfo[];
    NoData: boolean;
    ViewReady: boolean;
    ShowingWindowPanel: boolean;
    SmallSize: boolean;
    WindowsData: MatTableDataSource<NominationWindowInfo>;
    WindowEntryForm: FormGroup;
    User: UserInfo;
    FormWindow: boolean;
    WindowTypeVcSub: Subscription;

	constructor(private appService: ApplicationService, private awardService: AwardsService, private settingsSvc: SettingsService,
        private dateFnsPipe: DateFnsPipe, private dialogService: DialogService, private errorService: ErrorService,
        private fb: FormBuilder, private nomineeTypePipe: NomineeTypePipe, private screenSizeService: ScreenSizeService,
        private splashService: SplashService, private userService: UserService, private windowTypePipe: WindowTypePipe, private formTemplatePipe: FormTemplatePipe) { }

    ngOnInit() {
        const self = this;

        self.AvailableFormTemplates = null;
        self.AvailableNomineeTypes = null;
        self.AvailableWindowTypes = null;
        self.AwardInfo = null;
        self.CurrentSchoolYear = null;
        self.Windows = null;
        self.NoData = false;
        self.User = null;
        self.ViewReady = false;
        self.ShowingWindowPanel = false;
        self.FormWindow = false;
        self.WindowsData = new MatTableDataSource<NominationWindowInfo>([]);

        self.SmallSize = self.screenSizeService.ScreenSize.IsSmallSize;

        self.screenSizeService.ScreenSizeSub.pipe(
            takeUntil(self._subrelease)
        ).subscribe(size => {
            self.SmallSize = size.IsSmallSize;
        });

        // Get the first non-null UserInfo object so we can begin setting up the component
        self.userService.UserSubject.pipe(takeUntil(self._subrelease), first(user => user && !!user.AuthToken)).subscribe(user => {
			// Set up constant monitoring of the user object, to catch AuthToken updates.
			self.userService.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(_user => { self.User = _user; });

			// Load static data for use in the compoment.
			const curYearObs = self.settingsSvc.CurrentSchoolYearSubject;
			const curAwardObs = self.awardService.CurrentAwardsSub;
			const awdNomineeTypeObs = self.appService.GetNomineeTypes();
			const awdWindowTypeObs = self.appService.GetWindowTypes();
			const awdFormTemplateObs = self.appService.GetFormTemplates();

			combineLatest(curYearObs, curAwardObs, awdNomineeTypeObs, awdWindowTypeObs, awdFormTemplateObs).pipe(
				takeUntil(self._subrelease),
				map(packet => {
					return {
						CurrentYear: packet[0],
						CurrentAward: packet[1],
						NomineeTypes: packet[2],
						WindowTypes: packet[3],
						FormTemplates: packet[4]
					};
				})
			).subscribe(packet => {
				self.CurrentSchoolYear = packet.CurrentYear;
				self.AwardInfo = packet.CurrentAward;

				// Finish loading page info. In this case, filter down the nominee types and window types for this award program
				self.AvailableFormTemplates = packet.FormTemplates.filter(item => item.AwardID === self.AwardInfo.AwardID);
				self.AvailableNomineeTypes = packet.NomineeTypes.filter(item => item.AwardID === self.AwardInfo.AwardID);
				self.AvailableWindowTypes = packet.WindowTypes.filter(item => item.AwardID === self.AwardInfo.AwardID);

				// Setup the form group
				self.WindowEntryForm = self.fb.group({
					NominationWindowID: [null],
					NomineeTypeID: [null, Validators.required],
					WindowTypeID: [null, Validators.required],
					FormTemplateID: [null, Validators.required],
					DateBeginDate: [null, Validators.required],
					DateBeginTime: [null, [Validators.required, Validators.pattern(/^(0?[1-9]|1[0-2]):[0-5]\d [aApP][mM]$/)]],
					DateEndDate: [null, Validators.required],
					DateEndTime: [null, [Validators.required, Validators.pattern(/^(0?[1-9]|1[0-2]):[0-5]\d [aApP][mM]$/)]],
				});

				//Setup ValueChange subscription for WindowType
				self.WindowTypeVcSub = self.WindowEntryForm.get('WindowTypeID').valueChanges.pipe(takeUntil(self._subrelease)).subscribe(values => {
					let swt = self.AvailableWindowTypes.find(w => w.WindowTypeID === values);
					let selectedWindowType = (swt == null) ? null : swt.Description
					let formTemplateQuestion = self.WindowEntryForm.get('FormTemplateID');

					//Display Form Template question
					if (selectedWindowType === "Nomination Form") {
						formTemplateQuestion.enable();
						self.FormWindow = true;
					}
					else {
						self.FormWindow = false;
						formTemplateQuestion.disable();
					}
				});

				// Now, load the windows data.
				self.appService.GetNominationWindows(self.CurrentSchoolYear).pipe(takeUntil(self._subrelease)).subscribe(windows => {
					//only windows of this award type
					windows = windows.filter(w => w.AwardID == self.AwardInfo.AwardID);

					// Update the data source
					self.NoData = (!windows || !windows.length);
					self.Windows = (windows || []);
					self.WindowsData.data = (self.NoData ? [<NominationWindowInfo>{}] : windows);
					self.WindowsData.sort = self.sorter;
					self.WindowsData.paginator = self.paginator;

					self.ViewReady = true;
				}, error => self.errorService.HandleServiceError(error));
			}, error => self.errorService.HandleServiceError(error));
        });
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
        if (this.WindowTypeVcSub) this.WindowTypeVcSub.unsubscribe();
    }

    // #region Add Window Method

    AddWindow() {
        const self = this;

        const form = self.WindowEntryForm.getRawValue();

        if (!form || !self.WindowEntryForm.valid) { return; }

        // Create a valid datebegin and dateend based on the separate entries
        if (form.DateBeginDate) {
            if (typeof(form.DateBeginDate) === 'string') { form.DateBeginDate = new Date(form.DateBeginDate); }

            form.DateBegin = new Date(form.DateBeginDate.toLocaleDateString() + ' ' + form.DateBeginTime);
        } else {
            self.splashService.SnackbarAlert('Unable to find proper window opening date.');
            return;
        }

        if (form.DateEndDate) {
            if (typeof(form.DateEndDate) === 'string') { form.DateEndDate = new Date(form.DateEndDate); }

            form.DateEnd = new Date(form.DateEndDate.toLocaleDateString() + ' ' + form.DateEndTime);
        } else {
            self.splashService.SnackbarAlert('Unable to find proper window closing date.');
            return;
        }

        // Is the sleected window to be created one which already exists? If so, we cannot allow it.
        const foundWindow = self.Windows.filter(item =>
            item.SchoolYear === self.CurrentSchoolYear && item.NomineeTypeID === form.NomineeTypeID
                && item.WindowTypeID === form.WindowTypeID);

        let NA = "N/A";

        if (foundWindow && !!foundWindow.length) {
            self.dialogService.OpenMessageBoxDialog({
                TitleText: 'Window Already Exists',
                MessageText: `The window you are attempting to add to the system already exists:<br /><br />` +
                `<strong>School Year: </strong>${self.CurrentSchoolYear}<br />` +
                `<strong>Nominee Type: </strong>${self.nomineeTypePipe.transform(foundWindow[0].NomineeTypeID, self.AvailableNomineeTypes)}<br />` +
                `<strong>Form Template: </strong>${self.formTemplatePipe.transform(form.FormTemplateID, self.AvailableFormTemplates) || NA}<br />` +
                `<strong>Window Type: </strong>${self.windowTypePipe.transform(foundWindow[0].WindowTypeID, self.AvailableWindowTypes)}<br />` +
                `<strong>Window Opens: </strong>${self.dateFnsPipe.transform(foundWindow[0].DateBegin, 'eeee, M/d/yyyy h:mm a')}<br />` +
                `<strong>Window Closes: </strong>${self.dateFnsPipe.transform(foundWindow[0].DateEnd, 'eeee, M/d/yyyy h:mm a')}` +
                `<br /><br />You cannot create a duplicate window. If necessary, please edit the existing window.`,
                OKText: 'OK'
            }).pipe(takeUntil(self._subrelease)).subscribe();

            return;
        }

        // Get a confirmation before proceeding
        const msg = `You are about to add the following new nomination window:<br /><br />` +
        `<strong>Nominee Type: </strong>${self.nomineeTypePipe.transform(form.NomineeTypeID, self.AvailableNomineeTypes)}<br />` +
        `<strong>Form Template: </strong>${self.formTemplatePipe.transform(form.FormTemplateID, self.AvailableFormTemplates) || NA}<br />` +
        `<strong>Window Type: </strong>${self.windowTypePipe.transform(form.WindowTypeID, self.AvailableWindowTypes)}<br />` +
        `<strong>Window Opens: </strong>${self.dateFnsPipe.transform(form.DateBegin, 'eeee, M/d/yyyy h:mm a')}<br />` +
        `<strong>Window Closes: </strong>${self.dateFnsPipe.transform(form.DateEnd, 'eeee, M/d/yyyy h:mm a')}` +
        `<br /><br />Once the window is added, it will take effect immediately. Are you sure you wish to add ` +
        `this window as shown above?`;

        self.dialogService.OpenConfirmationDialog({
            TitleText: 'Confirmation Requested',
            MessageText: msg,
            OKText: 'Yes - Add Window',
            CancelText: 'No - CANCEL'
        }).pipe(takeUntil(self._subrelease)).subscribe(
            result => {
                if (result === true) {
                    // User has opted to proceed. Signal the system to add the window.
                    self.appService.AddWindow(self.User.AuthToken, self.User.CurrentRole, self.AwardInfo.AwardID, self.CurrentSchoolYear,
                        form.NomineeTypeID, form.WindowTypeID, form.FormTemplateID, form.DateBegin, form.DateEnd)
                        .pipe(takeUntil(self._subrelease)).subscribe(windows => {
                            // Toast the success
                            self.splashService.SnackbarSuccess('Nomination window added successfully.');

                            //only windows of this award type
                            windows = windows.filter(w => w.AwardID == self.AwardInfo.AwardID);

                            // Update the data source
                            self.NoData = (!windows || !windows.length);
                            self.Windows = (windows || []);
                            self.WindowsData.data = (self.NoData ? [<NominationWindowInfo>{}] : windows);
                            self.WindowsData.sort = self.sorter;
                            self.WindowsData.paginator = self.paginator;
                            self.table.renderRows();

                            // Reset the term form
                            self.WindowEntryForm.reset();

                            // Hide the panel
                            self.ShowingWindowPanel = false;
                        }, error => {
                            self.errorService.HandleServiceError(error);
                        });
                }
            }
        );
    }

    // #endregion

    // #region Cancel Window Panel Method

    CancelWindowPanel() {
        const self = this;

        // Null out the form, then close the panel.
        self.WindowEntryForm.reset();

        self.ShowingWindowPanel = false;
    }

    // #endregion

    // #region Show Window Panel Method

    ShowWindowPanel(win?: NominationWindowInfo) {
        const self = this;
        let formTemplateQuestion = self.WindowEntryForm.get('FormTemplateID');

        // Set up the formgroup to prepare for editing. If we were provided a nomination window, use those values to prefill the formgroup.
        if (win) {
            const patch = {
                NominationWindowID: win.NominationWindowID,
                NomineeTypeID: win.NomineeTypeID,
                WindowTypeID: win.WindowTypeID,
                FormTemplateID: win.FormTemplateID,
                DateBegin: win.DateBegin,
                DateEnd: win.DateEnd,
                DateBeginDate: win.DateBegin,
                DateBeginTime: self.dateFnsPipe.transform(win.DateBegin, 'h:mm a'),
                DateEndDate: win.DateEnd,
                DateEndTime: self.dateFnsPipe.transform(win.DateEnd, 'h:mm a')
            };

            // Patch the values
            self.WindowEntryForm.patchValue(patch);

            //See if windowType is "Nomination Form"
            let swt = self.AvailableWindowTypes.find(w => w.WindowTypeID === win.WindowTypeID);
            let selectedWindowType = (swt == null) ? null : swt.Description

            if (selectedWindowType === "Nomination Form") {
                formTemplateQuestion.enable();
                self.FormWindow = true;
            }
            else {
                self.FormWindow = false;
                formTemplateQuestion.disable();
            }

        } else {
            // Clear the form
            self.WindowEntryForm.reset();
            self.FormWindow = false;
            formTemplateQuestion.disable();
        }

        self.WindowEntryForm.updateValueAndValidity({emitEvent : false });

        self.ShowingWindowPanel = true;
    }

    // #endregion

    // #region Update Window

    UpdateWindow() {
        const self = this;

        const form = self.WindowEntryForm.getRawValue();

        if (!form || !self.WindowEntryForm.valid) { return; }

        // Create a valid datebegin and dateend based on the separate entries
        if (form.DateBeginDate) {
            if (typeof(form.DateBeginDate) === 'string') { form.DateBeginDate = new Date(form.DateBeginDate); }

            form.DateBegin = new Date(form.DateBeginDate.toLocaleDateString() + ' ' + form.DateBeginTime);
        } else {
            self.splashService.SnackbarAlert('Unable to find proper window opening date.');
            return;
        }

        if (form.DateEndDate) {
            if (typeof(form.DateEndDate) === 'string') { form.DateEndDate = new Date(form.DateEndDate); }

            form.DateEnd = new Date(form.DateEndDate.toLocaleDateString() + ' ' + form.DateEndTime);
        } else {
            self.splashService.SnackbarAlert('Unable to find proper window closing date.');
            return;
        }

        let NA = "N/A";

        // Get a confirmation before proceeding
        const msg = `You are about to update the following nomination window:<br /><br />` +
            `<strong>Nominee Type: </strong>${self.nomineeTypePipe.transform(form.NomineeTypeID, self.AvailableNomineeTypes)}<br />` +
            `<strong>Form Template: </strong>${self.formTemplatePipe.transform(form.FormTemplateID, self.AvailableFormTemplates) || NA}<br />` +
            `<strong>Window Type: </strong>${self.windowTypePipe.transform(form.WindowTypeID, self.AvailableWindowTypes)}<br />` +
            `<strong>Window Opens: </strong>${self.dateFnsPipe.transform(form.DateBegin, 'eeee, M/d/yyyy h:mm a')}<br />` +
            `<strong>Window Closes: </strong>${self.dateFnsPipe.transform(form.DateEnd, 'eeee, M/d/yyyy h:mm a')}` +
            `<br /><br />Once the window is added, it will take effect immediately. Are you sure you wish to add this window as shown above?`;

        self.dialogService.OpenConfirmationDialog({
            TitleText: 'Confirmation Requested',
            MessageText: msg,
            OKText: 'Yes - Update Window',
            CancelText: 'No - CANCEL'
        }).pipe(takeUntil(self._subrelease)).subscribe(result => {
            if (result === true) {
                // Update the window as requested.
				self.appService.UpdateWindow(self.userService.User.AuthToken, self.userService.User.CurrentRole, self.AwardInfo.AwardID,
					form.NominationWindowID, form.FormTemplateID, form.DateBegin, form.DateEnd, self.CurrentSchoolYear)
                    .pipe(takeUntil(self._subrelease)).subscribe(windows => {
                        // Toast the success
                        self.splashService.SnackbarSuccess('Nomination window updated successfully.');

                        // Update the list of known windows
                        self.Windows = windows;
                        self.WindowsData.data = windows;

                        self.WindowEntryForm.reset();
                        self.ShowingWindowPanel = false;
                }, error => self.errorService.HandleServiceError(error));
            }
        });
    }

    // #endregion
}
